import { ChangeEvent, FC, useCallback, useMemo } from 'react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { FieldState } from 'formstate';

import { GridCellProps } from '@progress/kendo-react-grid';

import { Button, Input, Table, TableColumn } from '@servicetitan/design-system';

import { AppAvailability, EmptyAppAvailability } from '../../api/app.api';

const makeInputCell: (
    readOnly: boolean,
    fieldState: FieldState<AppAvailability[]>,
) => FC<GridCellProps> = (readOnly, fieldState) => {
    const InputCell: FC<GridCellProps> = observer(props => {
        const dataIndex = props.dataIndex;
        const dataItem = props.dataItem as AppAvailability;
        const field = props.field as 'resourceOwner' | 'note';

        const handleChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                runInAction(() => {
                    fieldState.onChange(
                        fieldState.value.map((appAvailability, index) =>
                            index === dataIndex
                                ? { ...appAvailability, [field]: event.currentTarget.value }
                                : appAvailability,
                        ),
                    );
                });
            },
            [dataIndex, field],
        );

        return (
            <td role="gridcell">
                <Input
                    style={{ width: '100%' }}
                    maxLength={field === 'resourceOwner' ? 120 : 2000}
                    readOnly={readOnly}
                    value={dataItem[field]}
                    onChange={handleChange}
                />
            </td>
        );
    });
    InputCell.displayName = 'InputCell';
    return InputCell;
};

const makeActionCell: (
    disabled: boolean,
    fieldState: FieldState<AppAvailability[]>,
) => FC<GridCellProps> = (disabled, fieldState) => {
    const ActionCell: FC<GridCellProps> = ({ dataIndex }) => {
        const handleDelete = useCallback(() => {
            fieldState.onChange(
                fieldState.value.length === 1
                    ? [EmptyAppAvailability]
                    : fieldState.value.filter((_, index) => index !== dataIndex),
            );
        }, [dataIndex]);

        return (
            <td role="gridcell">
                <Button
                    iconName="delete"
                    fill="subtle"
                    aria-label="Delete"
                    disabled={
                        disabled ||
                        (fieldState.value.length === 1 &&
                            !fieldState.value[0].resourceOwner &&
                            !fieldState.value[0].note)
                    }
                    onClick={handleDelete}
                />
            </td>
        );
    };
    ActionCell.displayName = 'ActionCell';
    return ActionCell;
};

export interface AppAvailabilityTableProps {
    resourceOwnerType: string;
    readOnly: boolean;
    fieldState: FieldState<AppAvailability[]>;
}

export const AppAvailabilityTable: FC<AppAvailabilityTableProps> = observer(
    ({ resourceOwnerType, readOnly, fieldState }) => {
        const InputCell = useMemo(() => {
            return makeInputCell(readOnly, fieldState);
        }, [readOnly, fieldState]);

        const ActionCell = useMemo(() => {
            return makeActionCell(readOnly, fieldState);
        }, [readOnly, fieldState]);

        return (
            <Table data={fieldState.value} borders={{ horizontal: false }} striped={false}>
                <TableColumn
                    field="resourceOwner"
                    title={`${resourceOwnerType} ID`}
                    width="150"
                    cell={InputCell}
                />
                <TableColumn field="note" title="Note (optional)" cell={InputCell} />
                <TableColumn title="Delete" width="64" cell={ActionCell} />
            </Table>
        );
    },
);

AppAvailabilityTable.displayName = 'AppAvailabilityTable';
